import { createFocusTrap } from "focus-trap";
import { on, emit, getState, hydrate } from "@/neon/events";
import { formatMoney } from "@/neon/currency";

import { createItem, createRecent } from "@/lib/quick-cart";
import { getRecentProducts } from "@/lib/recentlyViewed";
import quickCartItem from "@/lib/quick-cart-item";
import { dispatchCustomEvent, CustomEvents } from "@/lib/dispatch-custom-event";
import { getCart } from "@/neon/cart";
import { animateCartDrawer } from "@/lib/animation";

function renderItems(items) {
  return items.length > 0
    ? items.reduce((markup, item) => {
        const hightlightProduct = getState().newItemInCart;

        // Only the first product should be highlighted. If a product
        // is highlighted set the flag to false after the first
        if (hightlightProduct) {
          hydrate({ newItemInCart: false });
        }

        markup += createItem(item, hightlightProduct);

        return markup;
      }, "")
    : `<p class="quick-cart__empty-state">${theme.strings.cart.general.empty}</p>`;
}

function renderDiscounts(discounts) {
  return discounts.length > 0
    ? `
    <div>
      ${discounts.map(
        ({ title, total_allocated_amount: value }) =>
          `<div>${title} (-${formatMoney(value)})</div>`
      )}
    </div>
  `
    : ``;
}

function renderRecent(products) {
  return products.length > 0
    ? products.reduce((markup, product) => {
        markup += createRecent(product);
        return markup;
      }, "")
    : `<p class="quick-cart__empty-state">${general.products.no_recently_viewed}</p>`;
}

const selectors = {
  cartItems: ".cart__item",
};

const cartDrawer = node => {
  if (!node) return;
  var focusTrap = createFocusTrap(node, { allowOutsideClick: true });

  const discounts = node.querySelector(".js-discounts");
  const subtotal = node.querySelector(".js-subtotal");
  const itemsRoot = node.querySelector(".js-items");
  const footer = node.querySelector(".js-footer");
  const closeIcon = node.querySelector(".js-close");
  const loading = itemsRoot.innerHTML;

  const tabLinkCart = node.querySelector(".js-tab-link-cart");
  const tabLinkRecent = node.querySelector(".js-tab-link-recent");

  const cartDrawerAnimation = animateCartDrawer(node);

  const render = cart => {
    const { cart_level_discount_applications: cart_discounts } = cart;
    tabLinkCart.classList.add("active");
    tabLinkRecent.classList.remove("active");

    itemsRoot.innerHTML = renderItems(cart.items);

    const cartItems = itemsRoot.querySelectorAll(selectors.cartItems);

    if (cartItems.length) {
      cartItems.forEach(item => {
        quickCartItem(item);
      });
    }

    if (getRecentProducts()) {
      tabLinkRecent.classList.remove("hide");
    }

    discounts.innerHTML = renderDiscounts(cart_discounts);
    cart.items.length > 0
      ? footer.classList.add("active")
      : footer.classList.remove("active");

    Boolean(cart_discounts.length > 0)
      ? discounts.classList.add("active")
      : discounts.classList.remove("active");

    if (subtotal) {
      subtotal.innerHTML = formatMoney(cart.total_price);
    }

    cartDrawerAnimation?.setup();
  };

  const open = cart => {
    node.classList.add("is-visible");
    emit("drawerOverlay:show");

    node.classList.add("is-active");
    itemsRoot.innerHTML = loading;

    emit("cart:open", state => {
      return {
        cartOpen: true,
      };
    });

    closeIcon.setAttribute("aria-expanded", true);

    setTimeout(() => {
      getCart().then(cart =>
        dispatchCustomEvent(CustomEvents.quickCartOpen, { cart: cart })
      );
      focusTrap.activate();
      tabLinkCart.classList.add("active");
      setTimeout(() => {
        render(cart);
        setTimeout(() => {
          cartDrawerAnimation?.open();
        }, 10);
      }, 10);
    }, 50);
  };

  const close = (hideOverlay = true) => {
    hideOverlay && emit("drawerOverlay:hide");
    node.classList.remove("is-visible");

    document
      .querySelectorAll('[aria-controls="cart-flyout-drawer"]')
      .forEach(control => {
        control.setAttribute("aria-expanded", false);
      });

    emit("cart:close", state => {
      return {
        cartOpen: false,
      };
    });

    tabLinkRecent.classList.remove("active");
    tabLinkCart.classList.remove("active");
    setTimeout(() => {
      focusTrap.deactivate();
      hydrate({ cartOpen: false });
      dispatchCustomEvent(CustomEvents.quickCartClose);

      cartDrawerAnimation?.close();
    }, 50);
  };

  const overlayHideListener = on("drawerOverlay:hiding", () => close(false));

  const viewCart = () => {
    tabLinkCart.classList.add("active");
    tabLinkRecent.classList.remove("active");

    const cart = getState().cart;
    render(cart);
  };

  const viewRecent = products => {
    if (!products) return;

    tabLinkCart.classList.remove("active");
    tabLinkRecent.classList.add("active");
    footer.classList.remove("active");
    itemsRoot.innerHTML = renderRecent(products);
  };

  const handleKeyboard = e => {
    const cartOpen = getState().cartOpen;
    if (!cartOpen) {
      return;
    }

    if (e.key == "Escape" || (e.keyCode === 27 && drawerOpen)) {
      close();
    }
  };

  render(getState().cart);

  tabLinkCart.addEventListener("click", e => e.preventDefault() || viewCart());
  tabLinkRecent.addEventListener(
    "click",
    e => e.preventDefault() || viewRecent(getRecentProducts())
  );

  closeIcon.addEventListener("click", close);

  on("cart:toggle", ({ cart, cartOpen }) => {
    cartOpen ? open(cart) : close();
  });

  on("cart:updated", ({ cartOpen, cart }) => {
    // Rerender the cart list only if cart is open
    if (cartOpen) {
      render(cart);
    } else {
      hydrate({ cartOpen: true });
      open(cart);
    }
  });

  window.addEventListener("keydown", handleKeyboard);

  const unload = () => {
    window.removeEventListener("keydown", handleKeyboard);
    overlayHideListener();
  };

  return { unload, open };
};

export default cartDrawer;
